import viewModes from 'shared/constants/view-modes';
import { DEFAULT_LOCALE, AVAILABLE_LOCALES } from 'locale/config';
import DEVICE_TYPES from 'shared/constants/device-types';

import { invokeSafely } from './attempt';
import { xor as xorGuids } from '@wix/wix-vod-shared/src/common/utils/guid';
import settings from '../settings';
import { apllyBMOverrides } from 'bm-integration/utils/business-manager-api';
import getResizedImageUrl from '@wix/wix-vod-shared/src/common/utils/get-resized-image-url';
import isDefaultADIChannel from '@wix/wix-vod-shared/src/common/utils/is-default-adi-channel';

const WixSDKOriginal = window.Wix;

function getRandomGuid4() { //generate fake guid starting on badabada
  /* eslint-disable */
  return 'badabada-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}

function memoizeFirstResult(fn) {
  let result;

  return function () {
    if (!result) {
      result = fn.apply(this, arguments);
    }
    return result;
  };
}

function setChannelIdToPublicStore(channelId) {
  WixSDK.Data.Public.set('channelId', channelId, { scope: 'COMPONENT' })
    .then(
      _.partial(console.log, 'Channel id has been saved to Wix public storage'))
    .catch(
      _.partial(console.warn, 'Can not save channel id to Wix public storage'));
}


const WixSDK = WixSDKOriginal ? {
  ...WixSDKOriginal,

  addEventListener: _.get(WixSDKOriginal, 'addEventListener', _.noop),
  removeEventListener: WixSDKOriginal.removeEventListener || _.noop,

  currentMember() {
    if (WixSDKOriginal.Utils.getViewMode() === viewModes.SITE) {
      return new Promise(WixSDKOriginal.currentMember);
    }

    return Promise.resolve(null);
  },

  getCtToken() {
    return new Promise(WixSDKOriginal.getCtToken)
      .then(ctToken => {
        if (!ctToken) {
          return Promise.reject(null);
        }

        return ctToken;
      });
  },

  getBoundingRectAndOffsets() {
    return new Promise(WixSDKOriginal.getBoundingRectAndOffsets);
  },

  getSiteUrl() {
    const viewMode = WixSDKOriginal.Utils.getViewMode();

    return new Promise(resolve => {
      if (viewMode === viewModes.STANDALONE || viewMode === viewModes.DASHBOARD) {
        return WixSDKOriginal.Dashboard.getSiteViewUrl(siteViewUrl => resolve(siteViewUrl.base));
      }

      WixSDKOriginal.Settings.getSiteInfo(siteInfo => resolve(siteInfo.baseUrl));
    });
  },

  getCurrentPageId() {
    return new Promise(WixSDKOriginal.getCurrentPageId);
  },

  getComponentInfo() {
    return new Promise(WixSDKOriginal.getComponentInfo);
  },

  navigateToCurrentComponent() {
    WixSDKOriginal.navigateToComponent(WixSDKOriginal.Utils.getCompId());
  },

  Activities: {
    ...WixSDKOriginal.Activities,
    getUserSessionToken() {
      return new Promise(WixSDKOriginal.Activities.getUserSessionToken);
    },
  },

  Dashboard: {
    ...WixSDKOriginal.Dashboard,
    getEditorUrl() {
      const getEditorUrl = _.get(WixSDKOriginal, 'Dashboard.getEditorUrl');
      if (getEditorUrl) {
        return new Promise(getEditorUrl);
      }

      return Promise.resolve(null);
    },
  },

  Events: {
    ...WixSDKOriginal.Events,
  },

  Data: {
    ...WixSDKOriginal.Data,

    Public: {
      ..._.get(WixSDKOriginal, 'Data.Public', {}),

      get(key, options) {
        return new Promise(_.partial(WixSDKOriginal.Data.Public.get, key, options));
      },

      set(key, value, options) {
        return new Promise(_.partial(WixSDKOriginal.Data.Public.set, key, value, options));
      },

      remove(key, options) {
        return new Promise(_.partial(WixSDKOriginal.Data.Public.remove, key, options));
      },
    }
  },

  PubSub: {
    ...WixSDKOriginal.PubSub,
    subscribe: _.get(WixSDKOriginal, 'PubSub.subscribe', _.noop),
    unsubscribe: _.get(WixSDKOriginal, 'PubSub.unsubscribe', _.noop),
  },

  Settings: {
    ...WixSDKOriginal.Settings,

    setExternalId(id) {
      return new Promise((resolve, reject) => WixSDKOriginal.Settings.setExternalId(id, resolve, reject));
    },

    getSiteInfo() {
      const viewMode = WixSDKOriginal.Utils.getViewMode();

      if (viewMode === viewModes.STANDALONE || viewMode === viewModes.DASHBOARD) {
        return Promise.resolve({
          url: window.location.href,
        });
      }

      return new Promise(WixSDKOriginal.Settings.getSiteInfo);
    },

    getDashboardAppUrl() {
      return new Promise(WixSDKOriginal.Settings.getDashboardAppUrl);
    },
  },

  Styles: {
    ...WixSDKOriginal.Styles,

    setWidgetChannel() {
      return _.noop();
    },

    /**
     * This is tmp workaround to allow retrieve channel id in ADI while switching between different design presets
     * @param params
     * @returns {*}
     * @private
     */
    _extendStylesWithValuesFromPublicStore(params) {
      const appSettingsChannelId = _.get(params, 'fonts.channelId');

      if (!isDefaultADIChannel(appSettingsChannelId)) {
        return Promise.resolve(params);
      }

      return WixSDK.Data.Public.get('channelId', { scope: 'COMPONENT' })
        .then(({channelId}) => {
          console.log('Wix.Styles.getStyleParams: channel id has been taken from Wix public storage instead of style param', channelId);
          return {
            ...params,
            fonts: {
              ...params.fonts,
              channelId: channelId || appSettingsChannelId
            }
          };
        })
        .catch(() => params)
    },

    getStyleParams() {
      return new Promise(WixSDKOriginal.Styles.getStyleParams)
        .then(params => WixSDK.Styles._extendStylesWithValuesFromPublicStore(params));
    },

    setFontParam(key, value) {
      if (key === 'channelId' && value) {
        setChannelIdToPublicStore(value);
      }
      return new Promise(_.partial(WixSDKOriginal.Styles.setFontParam, key, { value }));
    },

    setBooleanParam(key, value) {
      return new Promise(_.partial(WixSDKOriginal.Styles.setBooleanParam, key, { value }));
    },

    setStyleParams(params) {
      const channelId = _.get(params, 'fonts.channelId');
      if (channelId) {
        setChannelIdToPublicStore(channelId);
      }
      return new Promise(_.partial(WixSDKOriginal.Styles.setStyleParams, params));
    },
  },

  Utils: {
    ...WixSDKOriginal.Utils,

    getCompId: _.get(WixSDKOriginal, 'Utils.getCompId', _.noop),
    getOrigCompId: _.get(WixSDKOriginal, 'Utils.getOrigCompId', _.noop),
    getDeviceType: _.get(WixSDKOriginal, 'Utils.getDeviceType', _.constant(DEVICE_TYPES.DESKTOP)),

    getBiToken: memoizeFirstResult(function getBiToken() {
      const biToken = WixSDKOriginal.Utils.getInstanceValue('biToken');

      if (biToken) {
        return biToken;
      }

      if (window.newrelic) {
        try {
          window.newrelic.addPageAction('empty-bi-token', {
            instanceId: WixSDK.Utils.getInstanceId(),
          });
        } catch (ex) {
          window.newrelic.noticeError(ex);
        }
      }

      return getRandomGuid4();
    }),

    getLocale() {
      // I really don't know why do we need here try/catch, but I'll leave it as it was
      try {
        const originalGetLocale = _.get(WixSDKOriginal, 'Utils.getLocale', _.noop);
        const locale = originalGetLocale();

        if (!locale || locale.length > 2) {
          // prevent xss
          return DEFAULT_LOCALE;
        }

        return (_.includes(AVAILABLE_LOCALES, locale)) ? locale : DEFAULT_LOCALE;
      } catch (error) {
        return DEFAULT_LOCALE;
      }
    },

    getInstance() {
      try {
        return location.search.match(/(?:\?|&)instance=([^&]+)/)[1];
      } catch (error) {
        return null;
      }
    },

    getInstanceId: memoizeFirstResult(function getInstanceId() {
      return WixSDK.Utils.getInstanceValue('instanceId') || getRandomGuid4();
    }),

    getInstanceValue: invokeSafely(function (key) {
      // if biToken is missing for any reason, WixSDK.Utils.getBiToken will generate fake one
      if (key === 'biToken') {
        return WixSDK.Utils.getBiToken();
      }

      if (key === 'appDefId') {
        if (WixSDK.Utils.getViewMode() === viewModes.STANDALONE) {
          return '143314e1-0660-f409-9aa7-6ed544ff1957'; // APP_DEFINITION_ID for DEV STANDALONE
        }
      }

      const result = WixSDKOriginal.Utils.getInstanceValue(key);

      if (key === 'siteOwnerId' && !result) {
        return WixSDKOriginal.Utils.getInstanceValue('uid');
      }

      return result;
    }),

    getMetaSiteIdFromBiToken() {
      let biToken;
      let instanceId;
      try {
        biToken = WixSDK.Utils.getInstanceValue('biToken');
        instanceId = WixSDK.Utils.getInstanceId();
      } catch (error) {
        // do nothing
      }

      if (biToken && instanceId) {
        return xorGuids(biToken, instanceId);
      }
    },

    getMetaSiteId() {
      const metaSiteId = WixSDK.Utils.getMetaSiteIdFromBiToken();
      if (metaSiteId) {
        return Promise.resolve(metaSiteId);
      }


      const viewMode = WixSDK.Utils.getViewMode();
      if (viewMode === viewModes.STANDALONE) {
        return Promise.reject('Unable to get metaSiteId in standalone view mode');
      }

      if (viewMode === viewModes.EDITOR) {
        return WixSDK.Settings.getDashboardAppUrl()
          .then(url => url.match(/\/sites\/([^/]+)/)[1]);
      }

      return WixSDK.Dashboard.getEditorUrl()
        .then(url => url.match(/metaSiteId=([^&]+)/)[1]);
    },

    getUid() {
      try {
        return WixSDKOriginal.Utils.getUid();
      } catch (error) {
      }

      try {
        return document.cookie.match(/wixClient=([^;]+)/)[1].split('|')[6];
      } catch (error) {
      }

      return null;
    },

    getViewMode: _.get(WixSDKOriginal, 'Utils.getViewMode', _.noop),

    isEditor() {
      return WixSDK.Utils.getViewMode() === viewModes.EDITOR;
    },

    Media: {
      getImageUrl(relativeUrl) {
        return `${settings.hosts.image}${relativeUrl}`;
      },

      getResizedImageUrl: (url, width, height = width, params = {}, mode) => getResizedImageUrl({
        url,
        width,
        height,
        params,
        mode,
        imageHost: settings.hosts.image,
      }),
    },
  },
} : null;

export default apllyBMOverrides(WixSDK);



// WEBPACK FOOTER //
// ./src/shared/utils/wix-sdk.js